import styled from "@emotion/styled";
import { CheckmarkIcon, CopyIcon } from "components/Icons";
import { PropsWithChildren } from "react";
import * as Sentry from "@sentry/browser";
import { Spacer } from "components/general/Spacer/Spacer";
import { SerializedStyles } from "@emotion/react";

interface CopyButtonProps {
  isCopied: boolean;
  setIsCopied: React.Dispatch<React.SetStateAction<boolean>>;
  copyValue: string;
  iconPlacement: "left" | "right";
  css?: SerializedStyles;
  iconSize?: number | string;
  iconGap?: number | string;
}

export const CopyButton = ({
  isCopied,
  setIsCopied,
  css,
  copyValue,
  iconPlacement,
  iconSize = "100%",
  iconGap = 4,
  children,
}: PropsWithChildren<CopyButtonProps>) => {
  const isCopiedIcon = isCopied ? (
    <CheckmarkIcon width={iconSize} height={iconSize} />
  ) : (
    <CopyIcon width={iconSize} height={iconSize} />
  );
  const content =
    iconPlacement === "left" ? (
      <>
        {isCopiedIcon}
        <Spacer width={iconGap} />
        {children}
      </>
    ) : (
      <>
        {children}
        <Spacer width={iconGap} />
        {isCopiedIcon}
      </>
    );
  return (
    <Button css={css} onClick={() => handleCopy(copyValue, setIsCopied)}>
      {content}
    </Button>
  );
};

// Sub-components

const Button = styled.button<{ css: SerializedStyles | undefined }>`
  border: none;
  display: flex;
  background-color: inherit;
  color: inherit;
  padding: 0;
  align-items: center;
  width: 100%;
  ${props => props.css}
`;

// Helpers

export const handleCopy = (
  value: string,
  setCopied: (value: React.SetStateAction<boolean>) => void
) => {
  if (value) {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 1000);
      })
      .catch(err => {
        console.log(err);
        Sentry.captureException(`Could not copy value to clipboard`, {
          extra: { value },
        });
      });
  }
};
